import styled from '@emotion/styled';
import {
  Select,
  MenuItem,
  TextField,
  Checkbox,
  IconButton,
  Divider,
  ListItemText,
} from '@mui/material';
import { colors } from 'theme/colors';
import FormControl from '@mui/material/FormControl';

export const StyledFormControl = styled(FormControl)(() => ({
  width: '100%',
}));

export const StyledMultiSelect = styled(Select)({
  '#select': {
    padding: '8px 16px',
    fontSize: 14,
  },
  '& .MuiFormControl-root ': {
    width: '100%',
  },
  '.MuiSelect-select': {
    padding: '5px 12px',
    border: 0,
    ':disabled': {
      background: `${colors.gray100} !important`,
      '& input': {
        color: colors.gray400,
        background: `${colors.gray100} !important`,
      },
    },
  },
  '.Mui-disabled': {
    background: `${colors.gray100} !important`,
    '.MuiSelect-select': {
      color: colors.gray400,
      background: `${colors.gray100} !important`,
    },
  },
  '.MuiMenuItem-root': {
    width: '100%',
    fontSize: 14,
    boxSizing: 'border-box',

    '& .MuiSelect-select': {
      height: 32,
      padding: 0,
      boxSizing: 'border-box',
    },
  },
  '& .MuiInputBase-input': {
    border: 0,
  },
  '& .MuiInputBase-root': {
    height: 32,
  },
  '#daysQuantity': {
    boxSizing: 'border-box',
    padding: '6px 12px',
    width: 65,
    height: 32,
    display: 'flex',
    alignItems: 'flex-end',
  },
  '& .MuiSelect-icon': {
    top: 'calc(50% - 12px)',
  },
  '& .MuiSelect-iconOpen': {
    transform: 'rotate(180deg)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.primary400,
    borderWidth: '2px',
  },
});

export const StyledInnerWrapper = styled.div`
  font-size: 14px;
  height: 32px;
  margin-bottom: 3px;

  & .MuiOutlinedInput-root {
    width: 100%;
    height: 32px;
    padding: 0;
    background-color: ${colors.white};

    & fieldset {
      border-color: ${colors.gray400};
      border-width: 1px;
    }

    &:hover:not(.Mui-focused, .Mui-disabled) fieldset {
      border-color: ${colors.primary400};
    }

    &.Mui-focused fieldset {
      border-color: ${colors.primary400};
      border-width: 2px;
    }

    &:active fieldset {
      border-color: ${colors.primary900};
      border-width: 1px;
    }

    &.Mui-error fieldset {
      border-color: ${colors.error800};
      border-width: 1px;
    }
  }

  & .MuiSelect-select {
    font-size: 14px;
    height: 32px;
    box-sizing: border-box;
    background-color: ${colors.white};
  }
`;

export const StyledSearchMenuItem = styled(MenuItem)(() => ({
  '&:hover': {
    backgroundColor: 'transparent',
  },
  cursor: 'default',
  height: '32px',
  minHeight: '32px',
  paddingLeft: '12px',
  paddingRight: '12px',
  paddingTop: 0,
  paddingBottom: 0,
  borderBottom: `2px solid ${colors.primary400}`,
}));

export const StyledSearchTextField = styled(TextField)(() => ({
  height: '32px',
  '& .MuiInputBase-root': {
    height: '32px',
    padding: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-input': {
    padding: 0,
    height: '32px',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
  },
}));

export const StyledClearIconButton = styled(IconButton)(() => ({
  padding: 0,
  width: '24px',
  height: '24px',
}));

export const StyledClearButton = styled(IconButton)(() => ({
  maxHeight: '32px',
  maxWidth: '32px',
  marginRight: '24px',
}));

export const StyledSelectAllMenuItem = styled(MenuItem)(() => ({
  height: '32px',
  minHeight: '32px',
  paddingLeft: '12px',
  '&.Mui-selected': {
    backgroundColor: 'transparent',
  },
  '&.Mui-selected:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

export const StyledCheckbox = styled(Checkbox)(() => ({
  padding: 0,
  marginRight: '8px',
  '&.Mui-checked': {
    color: colors.primary700,
  },
  '& .MuiSvgIcon-root': {
    width: '16px',
    height: '16px',
  },
  '& svg': {
    width: '16px',
    height: '16px',
  },
}));

export const StyledListItemText = styled(ListItemText)(() => ({
  '& .MuiTypography-root': {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0%',
    color: colors.gray900,
  },
}));

export const StyledDivider = styled(Divider)(() => ({
  marginTop: '0 !important',
  marginBottom: '0 !important',
  marginLeft: '8px',
  marginRight: '8px',
  backgroundColor: colors.primary50,
}));

export const StyledSearchResultsMenuItem = styled(MenuItem)(() => ({
  height: '32px',
  minHeight: '32px',
  paddingLeft: '12px',
  paddingTop: 0,
  paddingBottom: 0,
  color: colors.primary500,
  fontSize: '14px',
  fontWeight: 400,
  opacity: '1 !important',
  cursor: 'default',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

export const StyledOptionMenuItem = styled(MenuItem)(() => ({
  height: '32px',
  minHeight: '32px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '12px',
  '&:first-of-type': {
    marginTop: 0,
  },
}));

export const getMenuProps = (width?: number) => ({
  disableScrollLock: true,
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const,
  },
  slotProps: {
    paper: {
      style: {
        border: `2px solid ${colors.primary400}`,
        marginTop: '4px',
        width: width || undefined,
      },
      sx: {
        '& .MuiList-root': {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    list: {
      autoFocusItem: false,
      sx: {
        paddingTop: 0,
        paddingBottom: 0,
        '& > div:last-child': {
          maxHeight: '268px',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '12px',
            borderRight: '4px solid transparent',
            backgroundClip: 'padding-box',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: colors.primary50,
            borderRadius: '4px',
            marginTop: '4px',
            marginBottom: '4px',
            borderRight: '4px solid transparent',
            backgroundClip: 'padding-box',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.primary400,
            borderRadius: '4px',
            border: `2px solid ${colors.primary50}`,
            borderRight: '4px solid transparent',
            backgroundClip: 'padding-box',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: colors.primary400,
          },
          '&::-webkit-scrollbar-button': {
            display: 'none',
          },
        },
      },
    },
  },
});
